/* Tabbed gallery (sections/tabbed-gallery.liquid).

   A real tablist: roving tabindex, arrows, Home and End. From 990px with
   "scroll" on, the page's own scroll picks the range — a tab press scrolls
   to that range's stretch of the runway rather than setting state, so the
   two inputs can never disagree. Below 990px there is no runway and a tab
   sets the range directly. */
if (!customElements.get('oorla-packaging')) {
  class OorlaPackaging extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.images = Array.from(this.querySelectorAll('[data-pack-image]'));
      this.captions = Array.from(this.querySelectorAll('[data-pack-caption]'));
      this.panel = this.querySelector('[role="tabpanel"]');
      this.rail = this.querySelector('[role="tablist"]');
      this.counter = this.querySelector('[data-pack-counter]');
      this.count = this.tabs.length;
      this.active = 0;
      this.scrollMode = this.hasAttribute('data-scroll');
      this.desktop = window.matchMedia('(min-width: 990px)');
      if (!this.count) return;

      this.onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab) this.go(this.tabs.indexOf(tab));
      };
      this.onKeyDown = (event) => {
        const keys = {
          ArrowRight: this.active + 1,
          ArrowLeft: this.active - 1,
          Home: 0,
          End: this.count - 1,
        };
        if (!(event.key in keys)) return;
        event.preventDefault();
        this.go(keys[event.key]);
      };
      this.rail.addEventListener('click', this.onClick);
      this.rail.addEventListener('keydown', this.onKeyDown);

      /* Theme editor: selecting a range block shows it. */
      this.onBlockSelect = (event) => {
        const index = this.captions.indexOf(event.target);
        if (index >= 0) this.set(index);
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      /* "Scroll to discover" is only true while the tab row overflows. */
      this.measure = () => {
        this.classList.toggle('oorla-pack--overflows', this.rail.scrollWidth > this.rail.clientWidth + 4);
      };
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.rail);

      if (this.scrollMode) {
        this.frame = 0;
        this.onScroll = () => {
          if (!this.frame) this.frame = requestAnimationFrame(() => this.read());
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.read();
      }
    }

    disconnectedCallback() {
      if (this.rail) {
        this.rail.removeEventListener('click', this.onClick);
        this.rail.removeEventListener('keydown', this.onKeyDown);
      }
      if (this.onBlockSelect) this.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    get scrollDriven() {
      return this.scrollMode && this.desktop.matches;
    }

    read() {
      this.frame = 0;
      if (!this.scrollDriven) return;
      const box = this.getBoundingClientRect();
      const travel = box.height - window.innerHeight;
      if (travel <= 0) return;
      const done = Math.min(1, Math.max(0, -box.top / travel));
      this.set(Math.min(this.count - 1, Math.floor(done * this.count)));
    }

    offsetOf(index) {
      const top = this.getBoundingClientRect().top + window.scrollY;
      const travel = this.offsetHeight - window.innerHeight;
      return top + (travel * (index + 0.5)) / this.count;
    }

    go(next) {
      const index = (next + this.count) % this.count;
      if (this.scrollDriven) {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: this.offsetOf(index), behavior: reduced ? 'auto' : 'smooth' });
      } else {
        this.set(index);
      }
      this.tabs[index].focus();
    }

    set(index) {
      if (index === this.active && this.tabs[index].getAttribute('aria-selected') === 'true') return;
      this.active = index;
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.images.forEach((image, i) => {
        image.hidden = i !== index;
      });
      this.captions.forEach((caption, i) => {
        caption.hidden = i !== index;
      });
      if (this.panel) this.panel.setAttribute('aria-labelledby', this.tabs[index].id);
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
    }
  }

  customElements.define('oorla-packaging', OorlaPackaging);
}
